import { FC, useState, useRef, useEffect, type JSX } from "react";
import styles from "./StockTradingSignals.module.scss";
import dayjs from "dayjs";
import { DataGrid } from "@/components/data-grid/DataGrid";
import Module from "@/components/module/Module";
import StockTradingSignalsChart, { type SignalFlag } from "@/components/stock-trading-signals-chart/StockTradingSignalsChart";
import { getStockDataGridTradingSignals } from "@/controllers/trading-signals";
import { classNames } from "@/lib/utils";
import { Stock } from "@/models/stock";
import { NoPaddingType } from "@/models/module";
import { StockTradingSignalsRow } from "@/models/stock-trading-signals";
import { getStockTradingSignalsFlags } from "@/controllers/stock-trading-signals";
import SnackBar from "../snack-bar/SnackBar";

// Define the noPadding constant for the module
const noPadding: NoPaddingType = { source: "body", type: "mobile" };

// Define the props interface for the StockTradingSignals component
interface PropsTypes {
    stock: Stock;
}

// Define the StockTradingSignals component
const StockTradingSignals: FC<PropsTypes> = ({ stock }) => {
    const signalsRef = useRef<any[]>([]);
    const [focusTime, setFocusTime] = useState<number | null>(null);

    const [tradingSignalsFilters, setTradingSignalsFilters] = useState<JSX.Element>(null);
    const [appliedFilters, setAppliedFilters] = useState<{ items: any[]; page: number }>({ items: [], page: 0 });
    const [newDate, setNewDate] = useState<number>();
    const [flags, setFlags] = useState<SignalFlag[]>([]);
    const [activeFlags, setActiveFlags] = useState<SignalFlag[]>([]);
    const [historicalFlags, setHistoricalFlags] = useState<SignalFlag[]>([]);

    const [snackBar, setSnackBar] = useState<{ open: boolean; text: string; severity: "success" | "error" | "info" | "warning" }>({
        open: false,
        text: "",
        severity: "success",
    });

    const minDate = useRef<number>(1672531200000);
    // Function to generate trading signals legends based on the provided row data
    const getTradingSignalsLegends = (row): SignalFlag & { row_id: number | string } => {
        const { ["b/s"]: bs, sug_hold, sip_id: id, significance, return_pc: meanReturn, type, st_date: x } = row;
        return { x, id, row_id: row.id, ["b/s"]: bs, sip_id: id, type, significance, return: meanReturn, days: sug_hold };
    };

    // Function to add a series to the chart based on the clicked row data
    const addSerieToChart = (_: string, row: StockTradingSignalsRow) => {
        if (!row.st_date) return setSnackBar({ open: true, text: "Hold on a moment! We can't add that signal to the chart because it hasn't been triggered.", severity: "error" });
        const index = signalsRef.current ? signalsRef.current.findIndex(({ row_id }) => row_id === row.id) : -1;
        if (dayjs(row.st_date).isBefore(dayjs().subtract(12, "month"))) {
            setNewDate(row.st_date);
        }
        const rowClicked = getTradingSignalsLegends(row);
        if (index >= 0) {
            signalsRef.current.splice(index, 1);
            setActiveFlags((prevFlags) => prevFlags.filter((el) => el.id !== rowClicked.id));
        } else {
            if (rowClicked.x < minDate.current) {
                minDate.current = rowClicked.x;
            }
            signalsRef.current.push(rowClicked);
            setActiveFlags((prevFlags) => [...prevFlags, rowClicked]);
        }
        // Centre both charts on the signal.
        setFocusTime(rowClicked.x);
    };

    // Fetch stock trading signals flags whenever the appliedFilters, stock.stock_id, or newDate change
    useEffect(() => {
        if (appliedFilters.page > 0) {
            getStockTradingSignalsFlags(stock.stock_id, appliedFilters).then((flags) => {
                // 1672531200000 = Sunday, January 1, 2023 12:00:00 AM
                setFlags(flags);
                setActiveFlags([flags.pop()]);
                setHistoricalFlags(flags.filter((flag) => flag.x > minDate.current));
            });
        }
    }, [appliedFilters, stock.stock_id]);

    useEffect(() => {
        setHistoricalFlags(flags.filter((flag) => flag.x > minDate.current));
    }, [newDate]);

    // Render the StockTradingSignals component
    return (
        <div className={styles.StockTradingSignals}>
            <div className={styles.table}>
                <Module title={`Recent Trading Signals in ${stock.exchangesymbol}:${stock.ticker}`} help={{ moduleId: "recent_trading_signals", modulePage: "V2_stock_trading_signals" }} allowFullscreen filters={tradingSignalsFilters}>
                    <DataGrid
                        id={stock.stock_id}
                        fetcher={getStockDataGridTradingSignals}
                        button={{ onClick: addSerieToChart, series: signalsRef.current, tsFlags: true }}
                        applyFiltersToChart={setAppliedFilters}
                        setFilters={setTradingSignalsFilters}
                    />
                </Module>
            </div>
            <div className={styles["bottom-content"]}>
                <div className={classNames(styles.chart, styles["signals-chart"])}>
                    <Module title={`Latest Signal in ${stock.exchangesymbol}:${stock.ticker}`} help={{ moduleId: "latest_signals_chart", modulePage: "V2_stock_trading_signals" }} noPadding={noPadding} key="latest-signals">
                        <StockTradingSignalsChart stock={stock} type="latest" key="latest" focusTime={focusTime} newDate={newDate} appliedFilters={appliedFilters} flags={activeFlags} />
                    </Module>
                </div>
                <div className={classNames(styles.chart, styles["returns-chart"])}>
                    <Module
                        title={`Historical Signals in ${stock.exchangesymbol}:${stock.ticker}`}
                        help={{ moduleId: "historical_signals_chart", modulePage: "V2_stock_trading_signals" }}
                        noPadding={{ source: "body", type: "mobile" }}
                        key="historical-signals"
                        feedback
                    >
                        {" "}
                        <StockTradingSignalsChart appliedFilters={appliedFilters} stock={stock} type="historical" key="historical" focusTime={focusTime} newDate={newDate} flags={historicalFlags} />
                    </Module>
                </div>
            </div>
            <SnackBar open={snackBar.open} setOpen={(open) => setSnackBar({ ...snackBar, open })} severity={snackBar.severity} text={snackBar.text} />
        </div>
    );
};

export default StockTradingSignals;
